import { useState, useMemo, useCallback } from 'react';
import Map, { useControl, NavigationControl, Marker, FullscreenControl, Popup } from 'react-map-gl';
import { MapboxOverlay } from '@deck.gl/mapbox';
import { useTheme } from "@mui/material/styles";
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import MapIcon from '@mui/icons-material/Map';
import LayersIcon from '@mui/icons-material/Layers';
import 'mapbox-gl/dist/mapbox-gl.css';
import { GeoJsonLayer, PathLayer } from '@deck.gl/layers';
import { bbox } from '@turf/bbox';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import useSWR from 'swr';
import { FOCUS_COLORS, FOCUS_COLS, getWeightedAverage, contextLayerColors, GEOGRAPHIES_DEFAULT } from '../constants';
import SpringfieldTop100 from '../springfieldtop100.geojson'
import ChicagoTop100 from '../chicagotop100.geojson'

const ILLINOIS_BOUNDING_BOX = [[-91.49457, 36.95667], [-87.34174, 42.51075]]
const MAPBOX_STYLES = {
    'light': 'mapbox://styles/mapbox/light-v11',
    'dark': 'mapbox://styles/mapbox/dark-v11',
    'streets': 'mapbox://styles/mapbox/streets-v12',
    'navigation-night': 'mapbox://styles/mapbox/navigation-guidance-night-v4',
    'satellite': 'mapbox://styles/mapbox/satellite-streets-v12'
}

function getSegmentColor(value, quantiles) {
    if (quantiles) {
        if (value < quantiles[1]) {
            return FOCUS_COLORS[0]
        } else if (value >= quantiles[1] && value < quantiles[2]) {
            return FOCUS_COLORS[1]
        } else if (value >= quantiles[2] && value < quantiles[3]) {
            return FOCUS_COLORS[2]
        } else if (value >= quantiles[3] && value <= quantiles[4]) {
            return FOCUS_COLORS[3]
        } else {
            return [0, 0, 255]
        }
    } else {
        return [0, 0, 255]
    }
}

function DeckGLOverlay(props) {
    const overlay = useControl(() => new MapboxOverlay(props));
    overlay.setProps(props);
    return null;
}

const illinoisLayerTitles = {
    'COUNTY_NAM': 'County',
    'DISTRICT': 'District',
    'URBAN_NAME': 'Urban Area',
    'REGION': 'Region',
    'MPO_NAME': 'MPO'
}

export default function RoadsMap(
    { mapData, mapRef, setFromToTable, tableTabValue, setTableTabValue, mapWidth, mapFocus, showOnMapData, setShowOnMapData, showOnMapMonitoredData, setShowOnMapMonitoredData, shouldFitMapLayerBounds, setShouldFitMapLayerBounds, layerOption, year }
    // pass mapData as prop from TableAndMapModule
) {
    const theme = useTheme();
    // TileLayer URLs are made using environment variables from a '.env.development.local' file at the root of this project (this file is ignored for the GitHub repository)
    const [fromProperties, setFromProperties] = useState(null);
    const [popupProperties, setPopupProperties] = useState(null);
    const [mapStyle, setMapStyle] = useState(theme.palette.mode === 'light' ? 'streets' : 'navigation-night');
    const [showMapStyles, setShowMapStyles] = useState(false);
    const [markerCoordinates, setMarkerCoordinates] = useState(null);
    const [displayCountiesLayer, setDisplayCountiesLayer] = useState(false);
    const [displayDistrictsLayer, setDisplayDistrictsLayer] = useState(false);
    const [displayUrbanAreasLayer, setDisplayUrbanAreasLayer] = useState(false);
    const [displayRegionsLayer, setDisplayRegionsLayer] = useState(false);
    const [displayMPOsLayer, setDisplayMPOsLayer] = useState(false);

    const mapFocusFilteredData = useMemo(() => {
        if (mapFocus !== 'None' && typeof mapFocus === 'string' && Array.isArray(mapData) && mapData.length > 0) {
            return mapData.filter(d => d[mapFocus] > 0)
        } else {
            return null
        }
    }, [mapFocus, mapData])

    const mapFocusMaxValue = useMemo(() => {
        if (Array.isArray(mapFocusFilteredData) && mapFocusFilteredData.length > 0 && mapFocus !== 'None') {
            return mapFocusFilteredData.reduce((currentMax, d) => d[mapFocus] > currentMax ? d[mapFocus] : currentMax, 0)
        } else {
            return null
        }
    }, [mapFocusFilteredData, mapFocus])

    const mapFocusMinValue = useMemo(() => {
        if (Array.isArray(mapFocusFilteredData) && mapFocusFilteredData.length > 0 && mapFocus !== 'None') {
            return mapFocusFilteredData.reduce((currentMin, d) => d[mapFocus] < currentMin ? d[mapFocus] : currentMin, Infinity)
        } else {
            return null
        }
    }, [mapFocusFilteredData, mapFocus])

    const mapFocusQuantiles = useMemo(() => {
        if (typeof mapFocusMaxValue === 'number' && typeof mapFocusMinValue === 'number' && mapFocusMaxValue !== mapFocusMinValue) {
            const rangeSpace = (mapFocusMaxValue - mapFocusMinValue) / 4
            return [
                mapFocusMinValue,
                (mapFocusMinValue + rangeSpace),
                (mapFocusMinValue + (rangeSpace * 2)),
                (mapFocusMinValue + (rangeSpace * 3)),
                Math.ceil(mapFocusMaxValue)
            ]
        } else {
            return null
        }
    }, [mapFocusMinValue, mapFocusMaxValue])

    const mapFocusHeader = useMemo(() =>
        mapFocus !== 'None'
            ? FOCUS_COLS.find(c => c.accessorKey === mapFocus).header
            : null,
        [mapFocus])

    const getTooltip = useCallback(({ object, layer }) => (object && layer) && {
        html: layer.id.includes('illinois')
            ?   `<div>${illinoisLayerTitles[Object.keys(object.properties)[0]]}: ${Object.keys(object.properties)[0] === 'DISTRICT' ? GEOGRAPHIES_DEFAULT.districts[Object.values(object.properties)[0]] : Object.values(object.properties)[0]}</div>`
            :   `<div><strong>Inventory Key Route ID</strong>:</div>
                <div>${object.inventory}</div>
                <div><strong>Begin Milepoint</strong>:</div>
                <div>${object.beg_sta}</div>
                <div><strong>End Milepoint</strong>:</div>
                <div>${object.end_sta}</div>
                ${(mapFocus !== 'None' && mapFocusHeader && typeof object[mapFocus] === 'number')
                    ? `<div><strong>${mapFocusHeader}</strong>:</div>
                    <div>${object[mapFocus].toLocaleString()}</div>`
                    : ''
                }`,
        style: {
            backgroundColor: 'white',
            color: 'black',
            fontSize: '1rem',
            lineHeight: '1.5',
            maxWidth: '240px',
        }
    }, [mapFocus, mapFocusHeader])

    const getMonitoredRoadsTooltip = useCallback(({ object, layer }) => (object && layer) && {
        html: layer.id.includes('illinois')
            ? `<div>${illinoisLayerTitles[Object.keys(object.properties)[0]]}: ${Object.keys(object.properties)[0] === 'DISTRICT' ? GEOGRAPHIES_DEFAULT.districts[Object.values(object.properties)[0]] : Object.values(object.properties)[0]}</div>`
            : `<div><strong>Road Name</strong>:</div>
            <div>${object.properties?.roadname}</div>
            <div><strong>Top 100 ID</strong>:</div>
            <div>${object.properties?.top100id}`,

        style: {
            backgroundColor: 'white',
            color: 'black',
            fontSize: '1rem',
            lineHeight: '1.5',
            maxWidth: '240px',
        }
    }, [])

    const segmentsWithFromInventory = useMemo(() => {
        if (fromProperties !== null && typeof fromProperties === 'object' && Array.isArray(mapData)) {
            return mapData.filter(segment => segment.inventory === fromProperties.inventory)
        } else {
            return []
        }
    }, [mapData, fromProperties])

    const layer = new PathLayer({
        id: 'data-layer',
        data: mapData,
        getPath: d => d.geometry,
        getColor: [0, 0, 255],
        getWidth: 5,
        pickable: popupProperties === null,
        autoHighlight: true,
        highlightColor: [255, 0, 0],
        widthUnits: 'pixels',
        visible: segmentsWithFromInventory.length === 0 && showOnMapData.length === 0 && mapFocus === 'None' && tableTabValue !== 2,
        onClick: info => setPopupProperties(info),
    })

    const onAfterRender = () => {
        if (shouldFitMapLayerBounds && layer.isLoaded) {
            const bounds = layer.getBounds()
            if (bounds !== null) {
                setShouldFitMapLayerBounds(false) // set to false to prevent infinitely fitting bounds
                mapRef.current.fitBounds([bounds[0][0], bounds[0][1], bounds[1][0], bounds[1][1]], { padding: 30 })
            }
        }
    }

    const focusLayer = new PathLayer({
        id: 'focus-layer',
        data: mapFocusFilteredData,
        getPath: d => d.geometry,
        getColor: d => getSegmentColor(d[mapFocus], mapFocusQuantiles),
        getWidth: 5,
        pickable: popupProperties === null,
        autoHighlight: true,
        highlightColor: [255, 0, 0],
        widthUnits: 'pixels',
        visible: mapFocusFilteredData !== null && segmentsWithFromInventory.length === 0 && showOnMapData.length === 0 && tableTabValue !== 2,
        onClick: info => setPopupProperties(info),
    })

    const showOnMapFilteredData = useMemo(() => {
        if (Array.isArray(showOnMapData) && showOnMapData.length > 0 && Array.isArray(mapData) && mapData.length > 0) {
            const filteredData = mapData.filter(d => showOnMapData.includes(d.id))
            const coordinateObj = {
                type: 'LineString',
                coordinates: filteredData.reduce((newArray, d) => newArray.concat(d.geometry), [])
            }
            const [minLng, minLat, maxLng, maxLat] = bbox(coordinateObj)
            mapRef.current.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 30 })
            return filteredData
        } else {
            return []
        }
    }, [mapData, showOnMapData])

    const showOnMapLayer = new PathLayer({
        id: 'show-on-map-layer',
        data: showOnMapFilteredData,
        getPath: d => d.geometry,
        getColor: [255, 0, 0],
        getWidth: 5,
        pickable: popupProperties === null,
        autoHighlight: true,
        highlightColor: [255, 0, 0],
        widthUnits: 'pixels',
        visible: tableTabValue !== 2,
        onClick: info => setPopupProperties(info)
    })

    const selectedLayer = new PathLayer({
        id: 'selected-layer',
        data: segmentsWithFromInventory,
        getPath: d => d.geometry,
        pickable: popupProperties === null,
        autoHighlight: true,
        highlightColor: [255, 0, 0],
        getColor: [0, 255, 0],
        getWidth: 5,
        widthUnits: 'pixels',
        visible: tableTabValue !== 2,
        onClick: info => setPopupProperties(info)
    })

    const illinoisCountiesLayer = new GeoJsonLayer({
        id: 'illinoisCountiesLayer',
        data: 'https://services2.arcgis.com/aIrBD8yn1TDTEXoz/arcgis/rest/services/Counties/FeatureServer/0/query?where=1%3D1&outFields=COUNTY_NAM&outSR=4326&f=geoJSON',
        getFillColor: [...contextLayerColors[0], 100],
        getLineColor: contextLayerColors[0],
        getLineWidth: 3,
        lineWidthUnits: 'pixels',
        pickable: true,
        visible: displayCountiesLayer,
    })

    const illinoisDistrictsLayer = new GeoJsonLayer({
        id: 'illinoisDistrictsLayer',
        data: 'https://services2.arcgis.com/aIrBD8yn1TDTEXoz/arcgis/rest/services/IDOTDistricts/FeatureServer/0/query?where=1%3D1&outFields=DISTRICT&outSR=4326&f=geoJSON',
        getFillColor: [...contextLayerColors[1], 100],
        getLineColor: contextLayerColors[1],
        getLineWidth: 3,
        lineWidthUnits: 'pixels',
        pickable: true,
        visible: displayDistrictsLayer,
    })

    const illinoisUrbanAreasLayer = new GeoJsonLayer({
        id: 'illinoisUrbanAreasLayer',
        data: 'https://services2.arcgis.com/aIrBD8yn1TDTEXoz/arcgis/rest/services/IL_Urban_Areas/FeatureServer/0/query?where=1%3D1&outFields=URBAN_NAME&outSR=4326&f=geoJSON',
        getFillColor: [...contextLayerColors[2], 100],
        getLineColor: contextLayerColors[2],
        getLineWidth: 3,
        lineWidthUnits: 'pixels',
        pickable: true,
        visible: displayUrbanAreasLayer,
    })

    const illinoisRegionsLayer = new GeoJsonLayer({
        id: 'illinoisRegionsLayer',
        data: 'https://services2.arcgis.com/aIrBD8yn1TDTEXoz/arcgis/rest/services/IDOT_Regions/FeatureServer/0/query?where=1%3D1&outFields=REGION&outSR=4326&f=geoJSON',
        getFillColor: [...contextLayerColors[3], 100],
        getLineColor: contextLayerColors[3],
        getLineWidth: 3,
        lineWidthUnits: 'pixels',
        pickable: true,
        visible: displayRegionsLayer,
    })

    const illinoisMPOsLayer = new GeoJsonLayer({
        id: 'illinoisMPOsLayer',
        data: 'https://services2.arcgis.com/aIrBD8yn1TDTEXoz/arcgis/rest/services/MPO/FeatureServer/0/query?where=1%3D1&outFields=MPO_NAME&outSR=4326&f=geoJSON',
        getFillColor: [...contextLayerColors[4], 100],
        getLineColor: contextLayerColors[4],
        getLineWidth: 3,
        lineWidthUnits: 'pixels',
        pickable: true,
        visible: displayMPOsLayer,
    })

    const layerOptionName = layerOption !== '' ? GEOGRAPHIES_DEFAULT.mpos[layerOption] : ''
    const { data: monitoredMapData, error: mapDataError } = useSWR(
        (['Chicago', 'Springfield'].includes(layerOptionName) && year === '2021')
            ? `${window.location.origin}${layerOptionName === 'Chicago' ? ChicagoTop100 : ''}${layerOptionName === 'Springfield' ? SpringfieldTop100 : ''}`
            : null,
        
        async (urlKey) => {
          const res = await fetch(urlKey)
          if (res.ok) {
            const newJSON = await res.json()
            return newJSON
          } else {
            throw new Error('data fetching failed')
          }
        },
        {
          revalidateIfStale: false,
          revalidateOnFocus: false,
          revalidateOnReconnect: false,
          shouldRetryOnError: false,
        },
    )

    const showOnMapFilteredMonitoredData = useMemo(() => {
        if (Array.isArray(showOnMapMonitoredData) && showOnMapMonitoredData.length > 0 && Array.isArray(monitoredMapData?.features) && monitoredMapData.features.length > 0) {
            const filteredData = monitoredMapData.features.filter(d => showOnMapMonitoredData.includes(d.properties.top100id))
            console.log(filteredData)
            const coordinateObj = {
                type: "FeatureCollection",
                features: filteredData
            }
            const [minLng, minLat, maxLng, maxLat] = bbox(coordinateObj)
            mapRef.current.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 30 })
            return coordinateObj
        } else {
            return []
        }
    }, [monitoredMapData, showOnMapMonitoredData])

    const monitoredRoadsLayer = new GeoJsonLayer({
        id: 'monitoredRoadsLayer',
        data: monitoredMapData,
        getLineColor: [0, 0, 255],
        getLineWidth: 5,
        lineWidthUnits: 'pixels',
        visible: tableTabValue === 2 && showOnMapMonitoredData.length === 0,
        pickable: popupProperties === null,
        autoHighlight: true,
        highlightColor: [255, 0, 0],
    })

    const showOnMapMonitoredLayer = new GeoJsonLayer({
        id: 'show-on-map-monitored-layer',
        data: showOnMapFilteredMonitoredData,
        getLineColor: [255, 0, 0],
        getLineWidth: 5,
        pickable: popupProperties === null,
        lineWidthUnits: 'pixels',
        visible: tableTabValue === 2,
    })

    const handleMapStyleChange = (event) => {
        setMapStyle(event.target.value)
    }

    return (
        <Map
            id='roads-map'
            ref={mapRef}
            initialViewState={{
                latitude: 40.212,
                longitude: -89.580,
                zoom: 5
            }}
            maxZoom={18}
            minZoom={5}
            mapboxAccessToken={process.env.REACT_APP_MAPBOX_PUBLIC_TOKEN}
            style={{ width: `${mapWidth - 1}vw`, height: '80vh' }}
            mapStyle={MAPBOX_STYLES[mapStyle]}
            projection='mercator'
        >
            <DeckGLOverlay
                useDevicePixels={false}
                layers={[
                    illinoisMPOsLayer,
                    illinoisRegionsLayer,
                    illinoisUrbanAreasLayer,
                    illinoisDistrictsLayer,
                    illinoisCountiesLayer,
                    selectedLayer,
                    showOnMapMonitoredLayer,
                    monitoredRoadsLayer,
                    focusLayer,
                    showOnMapLayer,
                    layer,
                ]}
                getTooltip={tableTabValue === 2 ? getMonitoredRoadsTooltip : getTooltip}
                onAfterRender={onAfterRender}
            />
            {markerCoordinates &&
                <Marker longitude={markerCoordinates[0]} latitude={markerCoordinates[1]} style={{ zIndex: 3 }} />
            }
            {popupProperties?.coordinate &&
                <Popup
                    longitude={popupProperties.coordinate[0]}
                    latitude={popupProperties.coordinate[1]}
                    onClose={() => setPopupProperties(null)}
                    closeButton={false}
                    anchor='top-left'
                    style={{ color: 'black', zIndex: 2, textAlign: 'start', fontSize: '1rem', lineHeight: 1.5 }}
                >
                    <IconButton onClick={() => { setPopupProperties(null) }} color='inherit' disableRipple sx={{ padding: 0 }}>
                        <CloseIcon fontSize='small' />
                    </IconButton>
                    <div><strong>Inventory Key Route ID</strong>:</div>
                    <div>{popupProperties.object.inventory}</div>
                    <div><strong>Begin Milepoint</strong>:</div>
                    <div>{popupProperties.object.beg_sta}</div>
                    <div><strong>End Milepoint</strong>:</div>
                    <div>{popupProperties.object.end_sta}</div>
                    {(mapFocus !== 'None' && mapFocusHeader && typeof popupProperties.object[mapFocus] === 'number') &&
                        <>
                            <div><strong>{mapFocusHeader}</strong>:</div>
                            <div>{popupProperties.object[mapFocus].toLocaleString()}</div>
                        </>
                    }
                    <Stack direction='row' spacing={1}>
                        <button disabled={fromProperties !== null} onClick={() => {
                            setFromProperties(popupProperties.object)
                            setMarkerCoordinates(popupProperties.coordinate)
                            setPopupProperties(null)
                        }}>From here</button>
                        <button disabled={fromProperties === null || (fromProperties && popupProperties.object.inventory !== fromProperties.inventory)} onClick={() => {
                            console.log('from')
                            console.log(fromProperties)
                            console.log('to')
                            console.log(popupProperties)
                            const leastBegSTA = Math.min(fromProperties.beg_sta, popupProperties.object.beg_sta)
                            console.log('least beg sta:', leastBegSTA)
                            const greatestEndSTA = Math.max(fromProperties.end_sta, popupProperties.object.end_sta)
                            console.log('greatest end sta:', greatestEndSTA)

                            const segmentsBetweenFromAndTo = segmentsWithFromInventory.filter(segment => segment.beg_sta >= leastBegSTA && segment.end_sta <= greatestEndSTA)

                            console.log(segmentsBetweenFromAndTo)

                            setFromToTable(previousRows => [
                                ...previousRows,
                                {
                                    'inventory': fromProperties ? fromProperties.inventory : '',
                                    'year': segmentsBetweenFromAndTo[0].year,
                                    'beg_sta': leastBegSTA,
                                    'end_sta': greatestEndSTA,
                                    'aadt': getWeightedAverage(segmentsBetweenFromAndTo, 'aadt', 'seg_length'),
                                    'delay': segmentsBetweenFromAndTo.reduce((a, s) => a + s.delay, 0),
                                    'trkdelay': segmentsBetweenFromAndTo.reduce((a, s) => a + s.trkdelay, 0),
                                    'congcost': segmentsBetweenFromAndTo.reduce((a, s) => a + s.congcost, 0),
                                    'ttivmt': segmentsBetweenFromAndTo.reduce((a, s) => a + s.ttivmt, 0),
                                    'trkttivmt': segmentsBetweenFromAndTo.reduce((a, s) => a + s.trkttivmt, 0),
                                    'tti': getWeightedAverage(segmentsBetweenFromAndTo, 'tti', 'ttivmt'),
                                    'allspeed': getWeightedAverage(segmentsBetweenFromAndTo, 'allspeed', 'ttivmt'),
                                    'allrefspeed': getWeightedAverage(segmentsBetweenFromAndTo, 'allrefspeed', 'ttivmt'),
                                    'pti95': getWeightedAverage(segmentsBetweenFromAndTo, 'pti95', 'ttivmt'),
                                    'pti80': getWeightedAverage(segmentsBetweenFromAndTo, 'pti80', 'ttivmt'),
                                    'trktti': getWeightedAverage(segmentsBetweenFromAndTo, 'trktti', 'trkttivmt'),
                                    'trkspeed': getWeightedAverage(segmentsBetweenFromAndTo, 'trkspeed', 'trkttivmt'),
                                    'trkrefspeed': getWeightedAverage(segmentsBetweenFromAndTo, 'trkrefspeed', 'trkttivmt'),
                                    'trkpti95': getWeightedAverage(segmentsBetweenFromAndTo, 'trkpti95', 'trkttivmt'),
                                    'trkpti80': getWeightedAverage(segmentsBetweenFromAndTo, 'trkpti80', 'trkttivmt'),
                                    subRows: segmentsBetweenFromAndTo
                                }
                            ])
                            setFromProperties(null)
                            setTableTabValue(1) // open 'Selected Roads' tab after adding new data
                            setMarkerCoordinates(null)
                            setPopupProperties(null)
                        }}
                        >To here</button>
                        <button variant='outlined' disabled={fromProperties === null} onClick={() => {
                            setFromProperties(null)
                            setMarkerCoordinates(null)
                            setPopupProperties(null)
                        }}>Reset selection</button>
                    </Stack>
                </Popup>
            }
            <NavigationControl position='top-left' style={{ zIndex: 2 }} />
            <FullscreenControl />
            <div
                className='map-style-control'
                onMouseOver={() => { setShowMapStyles(true) }}
                onMouseOut={() => { setShowMapStyles(false) }}
            >
                {showMapStyles ?
                    <div>
                        Map Style
                        <div><label>
                            <input type='radio' name='map-style-radio' value='streets' onChange={handleMapStyleChange} checked={mapStyle === 'streets'} />
                            Streets
                        </label></div>
                        <div><label>
                            <input type='radio' name='map-style-radio' value='navigation-night' onChange={handleMapStyleChange} checked={mapStyle === 'navigation-night'} />
                            Navigation Night
                        </label></div>
                        <div><label>
                            <input type='radio' name='map-style-radio' value='dark' onChange={handleMapStyleChange} checked={mapStyle === 'dark'} />
                            Dark
                        </label></div>
                        <div><label>
                            <input type='radio' name='map-style-radio' value='light' onChange={handleMapStyleChange} checked={mapStyle === 'light'} />
                            Light
                        </label></div>
                        <div><label>
                            <input type='radio' name='map-style-radio' value='satellite' onChange={handleMapStyleChange} checked={mapStyle === 'satellite'} />
                            Satellite
                        </label></div>
                        <hr />
                        Context Layers
                        <div><label>
                            <input type='checkbox' id='counties-context' name='context-layers' checked={displayCountiesLayer} onChange={e => { setDisplayCountiesLayer(e.target.checked) }} />
                            <div style={{display: 'inline-block', width: '0.75rem', height: '0.75rem', border: '1px solid black', marginRight: '3px', backgroundColor: `rgb(${contextLayerColors[0].join(' ')})`}} />
                             Counties
                        </label></div>
                        <div><label>
                            <input type='checkbox' id='districts-context' name='context-layers' checked={displayDistrictsLayer} onChange={e => { setDisplayDistrictsLayer(e.target.checked) }} />
                            <div style={{display: 'inline-block', width: '0.75rem', height: '0.75rem', border: '1px solid black', marginRight: '3px', backgroundColor: `rgb(${contextLayerColors[1].join(' ')})`}} />
                             Districts
                        </label></div>
                        <div><label>
                            <input type='checkbox' id='urban-areas-context' name='context-layers' checked={displayUrbanAreasLayer} onChange={e => { setDisplayUrbanAreasLayer(e.target.checked) }} />
                            <div style={{display: 'inline-block', width: '0.75rem', height: '0.75rem', border: '1px solid black', marginRight: '3px', backgroundColor: `rgb(${contextLayerColors[2].join(' ')})`}} />
                             Urban Areas
                        </label></div>
                        <div><label>
                            <input type='checkbox' id='regions-context' name='context-layers' checked={displayRegionsLayer} onChange={e => { setDisplayRegionsLayer(e.target.checked) }} />
                            <div style={{display: 'inline-block', width: '0.75rem', height: '0.75rem', border: '1px solid black', marginRight: '3px', backgroundColor: `rgb(${contextLayerColors[3].join(' ')})`}} />
                             Regions
                        </label></div>
                        <div><label>
                            <input type='checkbox' id='mpos-context' name='context-layers' checked={displayMPOsLayer} onChange={e => { setDisplayMPOsLayer(e.target.checked) }} />
                            <div style={{display: 'inline-block', width: '0.75rem', height: '0.75rem', border: '1px solid black', marginRight: '3px', backgroundColor: `rgb(${contextLayerColors[4].join(' ')})`}} />
                             MPOs
                        </label></div>
                    </div>
                    : <LayersIcon htmlColor='black' sx={{ width: '20px' }} />
                }
            </div>
            <Tooltip title='Reset map view to Illinois' placement='right'>
                <button className='reset-map-view-button' onClick={() => {
                    mapRef.current.fitBounds(ILLINOIS_BOUNDING_BOX)
                    setShowOnMapData([])
                }}>
                    <MapIcon htmlColor='black' />
                </button>
            </Tooltip>
            {(mapFocusQuantiles && tableTabValue !== 2) &&
                <div style={{
                    position: 'absolute',
                    left: 10,
                    bottom: 30,
                    padding: 7,
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                    textAlign: 'start',
                    fontSize: '1rem',
                    zIndex: 2
                }}>
                    <b>Legend: {mapFocusHeader}</b>
                    <Stack direction='row' spacing={1} sx={{ padding: 0.5 }}>
                        <div style={{ width: 20, height: 7, backgroundColor: `rgba(${FOCUS_COLORS[0].join()})` }} />
                        <div>{`${mapFocusQuantiles[0].toLocaleString('en-US', { maximumFractionDigits: 2 })} - < ${mapFocusQuantiles[1].toLocaleString('en-US', { maximumFractionDigits: 2 })}`}</div>
                    </Stack>
                    <Stack direction='row' spacing={1} sx={{ padding: 0.5 }}>
                        <div style={{ width: 20, height: 7, backgroundColor: `rgba(${FOCUS_COLORS[1].join()})` }} />
                        <div>{`${mapFocusQuantiles[1].toLocaleString('en-US', { maximumFractionDigits: 2 })} - < ${mapFocusQuantiles[2].toLocaleString('en-US', { maximumFractionDigits: 2 })}`}</div>
                    </Stack>
                    <Stack direction='row' spacing={1} sx={{ padding: 0.5 }}>
                        <div style={{ width: 20, height: 7, backgroundColor: `rgba(${FOCUS_COLORS[2].join()})` }} />
                        <div>{`${mapFocusQuantiles[2].toLocaleString('en-US', { maximumFractionDigits: 2 })} - < ${mapFocusQuantiles[3].toLocaleString('en-US', { maximumFractionDigits: 2 })}`}</div>
                    </Stack>
                    <Stack direction='row' spacing={1} sx={{ padding: 0.5 }}>
                        <div style={{ width: 20, height: 7, backgroundColor: `rgba(${FOCUS_COLORS[3].join()})` }} />
                        <div>{`${mapFocusQuantiles[3].toLocaleString('en-US', { maximumFractionDigits: 2 })} - ${mapFocusQuantiles[4].toLocaleString('en-US', { maximumFractionDigits: 2 })}`}</div>
                    </Stack>
                </div>
            }
        </Map>
    )
}